import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { ChargingScheduleModel } from '../models'
import { fetchChargingSchedules } from '../api'
import { usePortalQuery } from 'src/Libs/api'

interface UseChargingSchedules {
  chargingSchedules: ChargingScheduleModel[] | null
  loading: boolean
  error?: Error | AuthFetchError
  hasError: boolean
}

/** Time in milliseconds */
const STALE_TIME = Infinity
const MAX_RETRY_INTERVAL = 30000
const MAX_RETRY_ATTEMPTS = 5

/**
 * Hook for fetching a vehicle's charging schedules
 */
export const useFetchChargingSchedules = (vehicleId: number, installationId: number): UseChargingSchedules => {
  const queryKey = ['vehicle-integration', 'charging-schedules', vehicleId]
  const authFetch = useAuthFetchWithResponse()

  const queryFn = async (): Promise<ChargingScheduleModel[] | null> => {
    const { error: fetchError, hasErrors, value } = await fetchChargingSchedules(vehicleId, installationId, authFetch)

    if (hasErrors || fetchError) {
      throw new Error(fetchError?.message)
    }
    return value
  }

  /** retries after 0.5, 1, 2, 4 and 8 seconds */
  const exponentialBackoffDelay = (attempt: number): number => Math.min(500 * 2 ** attempt, MAX_RETRY_INTERVAL)

  const { data, isLoading, isError } = usePortalQuery({
    queryKey,
    queryFn,
    retry: MAX_RETRY_ATTEMPTS,
    staleTime: STALE_TIME,
    retryDelay: exponentialBackoffDelay,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  })

  return { chargingSchedules: data ?? null, loading: isLoading, hasError: isError }
}
